import { XMarkIcon } from '@heroicons/react/24/outline';
import React, { useEffect, useCallback, useRef, useState, useId } from 'react';
import { createPortal } from 'react-dom';

import { BottomSheet } from './BottomSheet';
import { useDialogFocus } from './useDialogFocus';
import { IconButton } from '../Button';

import type {
  ModalProps,
  ModalHeaderProps,
  ModalBodyProps,
  ModalFooterProps,
  ModalSize,
} from './Modal.types';

import { useBodyScrollLock } from '@/hooks';
import { useBreakpoint } from '@/hooks';

/**
 * Size classes for modal width
 */
const sizeClasses: Record<ModalSize, string> = {
  sm: 'max-w-[400px]',
  md: 'max-w-[560px]',
  lg: 'max-w-[720px]',
  xl: 'max-w-[900px]',
  '2xl': 'max-w-[1152px]',
  full: 'max-w-full mx-4',
};

/**
 * ModalHeader component
 * Displays the title and optional close button for a modal
 */
export function ModalHeader({
  title,
  titleId,
  showCloseButton = true,
  onClose,
  children,
  className = '',
  ...props
}: ModalHeaderProps) {
  return (
    <div
      className={`
        flex items-center justify-between gap-3 px-6 py-4
        border-b border-stroke-subtle
        ${className}
      `.trim()}
      {...props}
    >
      {children ?? (
        <h2
          id={titleId}
          className="font-display text-heading-md font-semibold text-neutral-50"
        >
          {title}
        </h2>
      )}
      {showCloseButton && onClose && (
        <IconButton
          variant="ghost-muted"
          icon={<XMarkIcon />}
          onClick={onClose}
          // The negative margin keeps the 64px header: the button's box
          // overhangs the title's line rather than growing it.
          className="-m-2"
          aria-label="Close"
          data-dialog-close
        />
      )}
    </div>
  );
}

/**
 * ModalBody component
 * Scrollable content area for modal content.
 *
 * `Modal` already wraps its children in one, so pass `Modal` the content
 * itself: a second `ModalBody` inside doubles the padding.
 */
export function ModalBody({
  children,
  className = '',
  ...props
}: ModalBodyProps) {
  return (
    <div
      className={`
        px-6 py-4 overflow-y-auto
        flex-1
        ${className}
      `.trim()}
      {...props}
    >
      {children}
    </div>
  );
}

/**
 * ModalFooter component
 * Footer with action buttons
 */
export function ModalFooter({
  children,
  align = 'right',
  className = '',
  ...props
}: ModalFooterProps) {
  const alignmentClasses: Record<string, string> = {
    left: 'justify-start',
    center: 'justify-center',
    right: 'justify-end',
    between: 'justify-between',
  };

  return (
    <div
      className={`
        flex items-center gap-3 px-6 pt-3 pb-4
        border-t border-stroke-subtle
        ${alignmentClasses[align]}
        ${className}
      `.trim()}
      {...props}
    >
      {children}
    </div>
  );
}

/**
 * Modal component
 * A dialog that appears on top of the main content: centred from `md` up, and
 * a `BottomSheet` below it. The app never shows a centred modal on a phone
 * (layout-wireframes, "Breakpoint Behavior Matrix"), so every Modal becomes a
 * sheet there on its own — callers don't switch between the two. `sheetHeight`
 * sets how far the sheet rises.
 *
 * Features:
 * - Multiple size variants (sm, md, lg, xl, 2xl, full)
 * - A `header` slot for headers a plain title can't express
 * - A footer of actions, on the right or set apart (`footerAlign`)
 * - Named by its `title`, or by `aria-labelledby` / `aria-label`; warns in
 *   development when a dialog has no name
 * - Backdrop with click-to-close option
 * - Escape key to close
 * - Focus trap and body scroll lock
 * - Scale + fade enter/exit animation
 * - Accessible with proper ARIA attributes
 *
 * @example
 * ```tsx
 * <Modal
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   title="Confirm Action"
 *   size="md"
 *   footer={
 *     <>
 *       <Button variant="ghost" onClick={() => setIsOpen(false)}>Cancel</Button>
 *       <Button variant="primary" onClick={handleConfirm}>Confirm</Button>
 *     </>
 *   }
 * >
 *   <p>Are you sure you want to proceed?</p>
 * </Modal>
 * ```
 */
export function Modal(props: ModalProps) {
  const { isBelow } = useBreakpoint();

  if (!isBelow('md')) return <CentredModal {...props} />;

  const {
    closeOnBackdropClick = true,
    footerAlign = 'right',
    sheetHeight = 'auto',
    'data-testid': testId,
  } = props;

  return (
    <BottomSheet
      isOpen={props.isOpen}
      onClose={props.onClose}
      title={props.title}
      header={props.header}
      aria-labelledby={props['aria-labelledby']}
      aria-label={props['aria-label']}
      height={sheetHeight}
      closeOnBackdropClick={closeOnBackdropClick}
      // A dialog a stray click can't dismiss, a stray swipe can't either.
      swipeToClose={closeOnBackdropClick}
      closeOnEscape={props.closeOnEscape}
      showCloseButton={props.showCloseButton}
      className={props.className}
      footer={props.footer}
      footerAlign={footerAlign}
      id={props.id}
      initialFocusRef={props.initialFocusRef}
      preventScroll={props.preventScroll}
      scrim={props.scrim}
      data-testid={testId ?? 'modal'}
    >
      {props.children}
    </BottomSheet>
  );
}

/** The centred dialog `Modal` renders from `md` up. */
function CentredModal({
  isOpen,
  onClose,
  size = 'md',
  title,
  header,
  'aria-labelledby': ariaLabelledBy,
  'aria-label': ariaLabel,
  closeOnBackdropClick = true,
  closeOnEscape = true,
  showCloseButton = true,
  className = '',
  children,
  footer,
  footerAlign,
  id,
  centered = true,
  initialFocusRef,
  preventScroll = true,
  scrim = true,
  'data-testid': testId,
}: ModalProps) {
  const [isAnimating, setIsAnimating] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const generatedId = useId();
  const modalId = id ?? `modal-${generatedId}`;
  // One id names the dialog and labels its heading, so the two can't diverge.
  const titleId = title ? `${modalId}-title` : undefined;
  const labelledBy = ariaLabelledBy ?? titleId;

  // A dialog needs a name, or a screen reader announces it as just "dialog".
  useEffect(() => {
    if (process.env.NODE_ENV === 'production' || !isOpen) return;
    if (!title && !ariaLabelledBy && !ariaLabel) {
      console.warn(
        '[Modal] This dialog has no accessible name. Give it a `title`, or name it with `aria-labelledby` (a heading inside it) or `aria-label`.'
      );
    }
  }, [isOpen, title, ariaLabelledBy, ariaLabel]);

  // Handle open/close state with animation
  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      // Trigger enter animation after mount
      requestAnimationFrame(() => {
        setIsAnimating(true);
      });
    } else if (shouldRender) {
      setIsAnimating(false);
      // Wait for exit animation before unmounting
      const timer = setTimeout(() => {
        setShouldRender(false);
      }, 200);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isOpen, shouldRender]);

  // Focus on open, Tab kept inside, and handed back on close.
  const { onKeyDown: handleKeyDown } = useDialogFocus({
    isOpen,
    isRendered: shouldRender,
    containerRef: modalRef,
    initialFocusRef,
    focusFirstField: true,
  });

  // Handle escape key
  useEffect(() => {
    if (!isOpen || !closeOnEscape) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, closeOnEscape, onClose]);

  // Hold the page's scroll while open — counted with every other overlay's.
  useBodyScrollLock(isOpen && preventScroll);

  // Handle backdrop click
  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (closeOnBackdropClick && e.target === e.currentTarget) {
        onClose();
      }
    },
    [closeOnBackdropClick, onClose]
  );

  if (!shouldRender) return null;

  const modal = (
    <div
      className={`
        fixed inset-0 z-50
        flex items-${centered ? 'center' : 'start'} justify-center
        p-4 sm:p-6
        ${scrim ? 'backdrop-blur-scrim' : ''}
        ${scrim && isAnimating ? 'bg-scrim-strong' : 'bg-transparent'}
        transition-colors duration-normal
      `.trim()}
      onClick={handleBackdropClick}
      aria-hidden={!isOpen}
    >
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        // Focusable only programmatically — the last resort when a modal has
        // neither a field nor a ✕. It is not a control, so it paints no focus
        // ring (the browser's default one is blue).
        tabIndex={-1}
        aria-labelledby={labelledBy}
        aria-label={ariaLabel}
        id={modalId}
        data-testid={testId ?? 'modal'}
        onKeyDown={handleKeyDown}
        className={`
          relative w-full ${sizeClasses[size]}
          bg-contrast-600
          rounded-xl border border-stroke-default shadow-lift-modal
          focus:outline-none
          flex flex-col
          max-h-[90vh]
          ${
            isAnimating
              ? 'opacity-100 scale-100 translate-y-0'
              : 'opacity-0 scale-95 translate-y-4'
          }
          transform transition-all duration-normal ease-out
          ${className}
        `.trim()}
      >
        {/* Header */}
        {(title || header) && (
          <ModalHeader
            title={title}
            titleId={titleId}
            showCloseButton={showCloseButton}
            onClose={onClose}
          >
            {header && <div className="min-w-0 flex-1">{header}</div>}
          </ModalHeader>
        )}

        {/* Body */}
        <ModalBody>{children}</ModalBody>

        {/* Footer */}
        {footer && <ModalFooter align={footerAlign}>{footer}</ModalFooter>}
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}

export default Modal;
